// Plain-language wording for the gym door screens (front desk). Pure functions only - no React.
// Rows are the items of GET /hpc/admin/zk/access (or /access/member/<id>).

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseIso = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
};
const toIso = (d) => d.toISOString().slice(0, 10);

// "2026-10-15" -> "15 Oct"
export const shortDate = (iso) => {
  const d = parseIso(iso);
  return d ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` : "";
};

// "2026-10-20 09:30:00" -> "09:30"
export const timeOf = (value) => (value ? String(value).slice(11, 16) : "");

export const addDays = (iso, days) => {
  const d = parseIso(iso);
  if (!d) return "";
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
};

// Same day next month; clamps to the month end (31 Jan + 1 month = 28/29 Feb).
export const addMonths = (iso, months) => {
  const d = parseIso(iso);
  if (!d) return "";
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return toIso(d);
};

export const presetDates = (today) => [
  { key: "today", label: "Today only", date: today },
  { key: "week", label: "1 week", date: addDays(today, 7) },
  { key: "month", label: "1 month", date: addMonths(today, 1) },
];

// ---------------------------------------------------------------- kinds of rows
// handled  = door control can change it (members by rules, staff / device admins by hand)
// protected = super admin / protected PIN: shown, never changed
export const isHandled = (row) => row.kind === "member" || !!row.manual;
export const isProtected = (row) => !!row.protected;
export const isVisible = (row) => isHandled(row) || isProtected(row);
export const isStaffOrAdmin = (row) => !!row.manual || isProtected(row);

// What the device still needs (mirrors access.needed_action on the server).
export const neededAction = (row) => {
  const applied = row.applied || "unknown";
  if (row.decision === "block" && applied !== "blocked") return "block";
  if (row.decision !== "block" && applied === "blocked") return "unblock";
  return null;
};

export const inFlight = (row) => ["pending", "sent"].includes(row.cmd_status) && !!row.cmd_action;

// ---------------------------------------------------------------- door status chip
export const DOOR_STATUS = {
  allowed: { label: "Allowed", color: "#4caf50" },
  blocked: { label: "Blocked", color: "#f44336" },
  updating: { label: "Updating...", color: "#29b6f6" },
  problem: { label: "Problem", color: "#ff9800" },
};

export const doorStatus = (row) => {
  if (inFlight(row)) {
    return { key: "updating", ...DOOR_STATUS.updating,
      help: "The door is being updated. This usually takes less than a minute." };
  }
  if (row.attempts > 0) {
    const retry = row.next_attempt_at ? ` TEBA tries again at ${timeOf(row.next_attempt_at)}.` : " TEBA tries again soon.";
    return { key: "problem", ...DOOR_STATUS.problem,
      help: `The door did not confirm the change (${row.last_result || "no answer"}).${retry}` };
  }
  if (row.applied === "blocked") {
    return { key: "blocked", ...DOOR_STATUS.blocked, help: "This person cannot open the door." };
  }
  return { key: "allowed", ...DOOR_STATUS.allowed, help: "This person can open the door." };
};

// ---------------------------------------------------------------- plain reason
const byWhom = (o) => (o?.created_by ? ` by ${o.created_by}` : "");

export const plainReason = (row) => {
  const o = row.override;
  if (o && (row.reason === "override_allow" || (row.manual && o.action === "allow"))) {
    return `Let in${byWhom(o)} until ${shortDate(o.valid_until)}`;
  }
  if (o && row.reason === "override_block") return `Blocked${byWhom(o)} until ${shortDate(o.valid_until)}`;
  const expiry = row.effective_expiry_date ? shortDate(row.effective_expiry_date) : "";
  const map = {
    active: expiry ? `Membership active until ${expiry}` : "Membership active",
    expired: expiry ? `Membership expired on ${expiry}` : "Membership expired",
    expired_grace_period: expiry ? `Membership expired on ${expiry} (grace period)` : "Membership expired (grace period)",
    within_grace_days: expiry ? `Membership expired on ${expiry} - still in grace days` : "Expired - still in grace days",
    pending: "Membership pending - not activated yet",
    under_review: "Waiting for medical clearance",
    pending_allowed: "Membership pending - allowed in for now",
    under_review_allowed: "Waiting for medical clearance - allowed in for now",
    frozen: "On hold (frozen)",
    frozen_allowed: "On hold (frozen) - allowed",
    admin_suspended: "Suspended by admin",
    admin_cancelled: "Membership cancelled",
    admin_inactive: "Marked inactive",
    admin_blacklisted: "Blacklisted",
    expiry_unknown: "Expiry date missing - please check",
    unknown_status: "Unusual membership status - please check",
    super_admin: "Super admin - always allowed",
    protected: "Protected - always allowed",
    staff: "Staff - only blocked by hand",
    device_admin: "Device admin - only blocked by hand",
    outside_pilot: "Not part of the door test",
    unmapped: "Not linked to a member",
    needs_review: "Name does not match - link the PIN first",
    ignored: "Ignored PIN",
  };
  return map[row.reason] || "Not handled by door control";
};

// Second line when the door does not match yet. ctx: { mode, paused, guardTripped }
export const pendingNote = (row, ctx = {}) => {
  const need = neededAction(row);
  if (!need || ctx.mode === "off") return "";
  if (ctx.mode === "dryrun") return need === "block" ? "Would be blocked (test mode)" : "Would be let in (test mode)";
  if (inFlight(row)) return need === "block" ? "Being blocked..." : "Being let in...";
  if (ctx.paused) return "Door changes are paused";
  if (need === "block" && ctx.guardTripped) return "Will be blocked when you approve";
  return need === "block" ? "Will be blocked within a minute" : "Will be let in within a minute";
};

// ---------------------------------------------------------------- one action per row
export const actionFor = (row) => {
  if (!isHandled(row) || isProtected(row)) return null;
  if (row.override) return { key: "normal", label: "Back to normal", help: "Remove the exception and follow the normal rules again" };
  if (row.decision === "block") return { key: "let_in", label: "Let in", action: "allow", help: "Let this person in until a date" };
  return { key: "block", label: "Block", action: "block", help: "Stop this person opening the door until a date" };
};

export const isDeviceAdmin = (row) => String(row?.privilege ?? "").trim() === "2";

// ---------------------------------------------------------------- status line + cards
export const statusLine = (settings, paused) => {
  const mode = settings?.mode;
  if (mode === "on" && paused) return { severity: "error", text: "PAUSED - door changes stopped" };
  if (mode === "on") return { severity: "success", text: "Door control is ON" };
  if (mode === "dryrun") return { severity: "warning", text: "TEST MODE - nothing is changed at the door" };
  return { severity: "info", text: "Door control is OFF" };
};

// Rows waiting for the admin's OK (guard): would be blocked, nothing sent yet.
export const awaitingApproval = (row) => neededAction(row) === "block" && !inFlight(row);

export const summarize = (items, settings, guard) => {
  const dryrun = settings?.mode === "dryrun";
  const visible = items.filter(isVisible);
  return {
    blockedLabel: dryrun ? "Would be blocked" : "Blocked",
    blocked: dryrun
      ? visible.filter((r) => neededAction(r) === "block").length
      : visible.filter((r) => r.applied === "blocked").length,
    needOk: guard?.tripped ? guard.blocks_needed : 0,
    allowed: visible.filter((r) => doorStatus(r).key === "allowed" && !(dryrun && neededAction(r) === "block")).length,
    notHandled: items.length - visible.length,
  };
};

// Simple-view list filters (cards + chips).
export const SIMPLE_FILTERS = {
  all: () => true,
  blocked: (r, settings) => (settings?.mode === "dryrun" ? neededAction(r) === "block" : r.applied === "blocked"),
  allowed: (r) => doorStatus(r).key === "allowed",
  staff: isStaffOrAdmin,
  review: awaitingApproval,
};

export const matchesSearch = (row, text) => {
  const q = (text || "").trim().toLowerCase();
  if (!q) return true;
  return [row.full_name, row.device_name, row.membership_id, row.pin]
    .some((v) => String(v || "").toLowerCase().includes(q));
};

export const displayName = (row) => row.full_name || row.device_name || `PIN ${row.pin}`;
export const displayId = (row) => (row.membership_id ? `${row.membership_id} · PIN ${row.pin}` : `PIN ${row.pin}`);
